<template>
  <v-card
    class="d-flex justify-space-between align-center task-inline-creator-container px-4"
    data-tourid="task-input-creator"
    elevation="0"
    :outlined="!hideBorder"
  >
    <div
      class="d-flex justify-start align-center"
      :style="showDetailFields ? 'width:  38%;' : 'width: 78%'"
    >
      <v-btn
        icon
        class="mr-2 cursor-default"
        small
        v-if="loading && !hideCompleteRadio"
        color="blue-grey lighten-1"
        :ripple="false"
      >
        <v-icon size="24">
          {{ isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank' }}
        </v-icon>
      </v-btn>
      <v-btn
        icon
        class="mr-2"
        small
        v-else-if="!loading && !hideCompleteRadio"
        :ripple="false"
        :color="completeButtonColor"
        @click="handleCreateTaskInline(true)"
      >
        <v-icon size="24" :color="completeButtonColor">
          {{ isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank' }}
        </v-icon>
      </v-btn>
      <input
        v-model="task.name"
        data-private
        type="text"
        class="task-input-creator font-size-13 font-weight-500"
        :class="
          loading
            ? 'blue-grey--text text--lighten-2'
            : 'blue-grey--text text--darken-3'
        "
        ref="taskInput"
        :disabled="loading"
        :maxlength="$constants.TASK.nameMaxLength"
        :placeholder="$t('tasks.add_task')"
        @keydown.enter="handleCreateTaskInline(false)"
      />
    </div>
    <template v-if="showDetailFields && $vuetify.breakpoint.mdAndUp">
      <div class="d-flex justify-space-between align-center">
        <div>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-text-field
                v-on="on"
                dense
                hide-details
                readonly
                outlined
                clearable
                class="task-input-border caption font-weight-500 task-inline-input"
                :disabled="loading"
                :value="dueDateText"
                :placeholder="$t('filters.due_date')"
                @click:clear="handleDueDateChange(null)"
              >
                <template v-slot:prepend-inner>
                  <v-icon
                    v-if="!dueDateText"
                    size="24"
                    class="me-1"
                    color="blue-grey lighten-1"
                  >
                    mdi-calendar
                  </v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              no-title
              reactive
              :value="task.dueDate"
              @change="handleDueDateChange"
            />
          </v-menu>
        </div>

        <div style="width: 33%;">
          <AssigneesSearchPopover
            :selectedAccountIds="assignees"
            :disabled="loading"
            @change="handleAssigneesChange"
          >
            <ChipAvatar
              v-if="hasAssignee"
              disablePopover
              hideBorder
              small
              :ripple="false"
              :account="assignee"
              :vuetifyProps="{ close: true, disabled: loading }"
              @click:close="handleChange('assignees', [])"
            />
            <v-text-field
              v-else
              dense
              hide-details
              readonly
              outlined
              class="task-input-border task-inline-input caption"
              prepend-inner-icon="mdi-account-outline"
              :disabled="loading"
              :placeholder="$t('common.assignee_one')"
            >
              <template v-slot:prepend-inner>
                <v-icon
                  v-if="!dueDateText"
                  size="24"
                  class="me-1"
                  color="blue-grey lighten-1"
                >
                  mdi-account-outline
                </v-icon>
              </template>
            </v-text-field>
          </AssigneesSearchPopover>
        </div>
        <div style="width: 33%;" class="d-flex align-center">
          <v-select
            flat
            dense
            outlined
            hide-details
            class="font-size-13 task-input-border"
            append-icon=""
            :items="priorities"
            :value="task.priority"
            :disabled="loading"
            @change="handleChange('priority', $event)"
          >
            <template v-slot:selection>
              <div
                class="ma-0 pa-0 caption d-flex align-center justify-center"
                :class="COLORS_BY_PRIORITY[task.priority]"
              >
                <PriorityIcon
                  v-if="task.priority"
                  :style="loading ? 'opacity: .3' : 'opacity: 1'"
                  :priority="task.priority"
                />
                <v-icon
                  v-else
                  size="24"
                  class="me-1"
                  color="blue-grey lighten-1"
                >
                  mdi-alert-circle-outline
                </v-icon>

                <p
                  class="ma-0 pa-0 ms-1"
                  :style="loading ? 'opacity: .3' : 'opacity: 1'"
                  :class="{ 'font-weight-500': !task.priority }"
                >
                  {{ priorityText }}
                </p>
              </div>
            </template>
          </v-select>
        </div>
      </div>
    </template>
    <div class="d-flex justify-end align-center" style="width: 10%">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            elevation="0"
            class="add-details-button me-2"
            :disabled="loading"
          >
            <v-icon
              size="16"
              color="blue-grey darken-1"
              @click="handleAddMoreDetails"
            >
              mdi-plus
            </v-icon>
          </v-btn>
        </template>
        {{ $t('routines.add_more_details') }}
      </v-tooltip>
      <v-btn
        small
        icon
        elevation="0"
        color="primary"
        v-if="!loading"
        :disabled="disableCreate"
        @click="handleCreateTaskInline(false)"
      >
        <v-icon size="16">
          mdi-send
        </v-icon>
      </v-btn>
      <v-btn v-if="loading" small icon elevation="0" color="primary" loading>
      </v-btn>
    </div>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
import * as moment from 'moment'
import _ from 'lodash'

import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import api from '@/api'
import getEntityId from '@/lib/getEntityId'

import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import PriorityIcon from '@/components/PriorityIcon.vue'

const COLORS_BY_PRIORITY = {
  low: 'blue--text',
  medium: 'amber--text darken-2',
  high: 'red--text',
  null: 'blue-grey--text text--lighten-1',
}

export default {
  name: 'InlineCreator',

  mixins: [mixin, trackerMixin],

  components: {
    AssigneesSearchPopover,
    ChipAvatar,
    PriorityIcon,
  },

  props: {
    shouldAutoAssign: {
      type: Boolean,
      default: true,
    },
    entityId: {
      type: String,
      require: false,
      default: undefined,
    },
    entityType: {
      type: String,
      require: false,
      default: undefined,
    },
    showDetailFields: Boolean,
    hideCompleteRadio: Boolean,
    hideBorder: Boolean,
  },

  data() {
    return {
      COLORS_BY_PRIORITY,
      priorities: [
        {
          value: 'high',
          text: this.$t('rev.priority.high'),
        },
        {
          value: 'medium',
          text: this.$t('rev.priority.medium'),
        },
        {
          value: 'low',
          text: this.$t('rev.priority.low'),
        },
        {
          value: null,
          text: this.$t('common.priority'),
        },
      ],
      task: {
        name: '',
        dueDate: null,
        assignees: [],
        priority: null,
      },
      loading: false,
      isCompleted: false,
    }
  },

  mounted() {
    this.setDefaultTaskInfo()
  },

  watch: {
    shouldAutoAssign: {
      immediate: true,
      handler(newVal) {
        if (!newVal) this.task.assignees = []
        if (newVal) this.task.assignees = [this.account]
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    completeButtonColor() {
      if (this.isCompleted) return 'green'
      if (this.task.name) return 'blue-grey text--darken-1'
      return 'blue-grey lighten-1'
    },

    priorityText() {
      return this.priorities.find(
        priority => this.task.priority === priority.value
      ).text
    },

    hasAssignee() {
      return !_.isEmpty(this.task?.assignees)
    },

    dueDateText() {
      if (!this.task?.dueDate) return null

      return utils.dates.formatDate(
        moment(this.task.dueDate).toISOString(),
        'MMM DD, YYYY'
      )
    },

    disableCreate() {
      return !this.task?.name?.trim()
    },

    assigneeId() {
      return this.task?.assignees?.[0]?.id ?? null
    },

    assignee() {
      return this.task?.assignees?.[0] ?? null
    },

    assignees() {
      if (!this.task.assignees.length) return []
      return this.task?.assignees.map(
        assignee => assignee?._id ?? assignee?.id ?? assignee
      )
    },
  },

  methods: {
    handleAssigneesChange(assignees) {
      this.task.assignees = assignees?.items ?? []
      this.trackTasks('tasks.assigneesChange', {
        hasAssignee: Boolean(assignees?.items?.length),
      })
    },

    handleChange(taskProperty, value) {
      this.task[taskProperty] = value
      this.trackTasks('tasks.priorityChange', {
        priorityType: value,
      })
    },

    handleDueDateChange(dueDate) {
      this.task = { ...this.task, dueDate }
      this.trackTasks('tasks.dueDateChange', {
        dueDateAssigned: dueDate,
      })
    },

    checkPrivacy() {
      if (this.entityId) return false
      return this.task.isPrivate ?? true
    },

    async handleCreateTaskInline(isCompleted) {
      try {
        if (this.loading || !this.task.name) return
        this.loading = true
        this.isCompleted = isCompleted

        await api.tasks.create(this.organizationId, {
          ...this.task,
          dueDate: moment(this.task.dueDate).toISOString(),
          isPrivate: this.checkPrivacy(),
          assignees: this.assignees,
          status: this.isCompleted
            ? this.$constants.tasksStatus.completed
            : this.$constants.tasksStatus.open,
          site: this.account?.site?._id,
          tags: this.account?.tags?.map(getEntityId),
          entityId: this.entityId,
          entityType: this.entityType,
        })

        this.$emit('taskWasCreated')
        this.trackTasks('tasks.create', {
          from: `${this.$route?.name}_inlineCreator`,
          nameLength: this.task?.name.length,
          dueDate: this.task?.dueDate,
          priority: this.task?.priority,
          status: this.isCompleted
            ? this.$constants.tasksStatus.completed
            : this.$constants.tasksStatus.open,
          hasDescription: false,
          hasMedia: false,
          hasDueDate: Boolean(this.task?.dueDate),
          hasAssignee: Boolean(this.task.assignees.length),
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
        this.isCompleted = false
        this.setDefaultTaskInfo()
      }
    },

    setDefaultTaskInfo() {
      this.task = {
        name: '',
        dueDate: null,
        priority: null,
        assignees: this.shouldAutoAssign ? [this.account] : [],
        site: this.account?.site?._id,
        tags: this.account.tags,
      }
    },

    handleAddMoreDetails() {
      this.$emit('addDetails', this.task)
      this.setDefaultTaskInfo()
      this.trackTasks('openCreateDrawer', {
        from: `${this.$route?.name}_addDetails`,
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.borders {
  border-left: 1px solid transparent;
}

.disabled {
  opacity: 0.4;
}

.bubble {
  &:hover {
    box-shadow: inset 18px 18px 18px 18px $color-blue-grey-lighten2 !important;
    border-radius: 50%;
  }
  width: 30px;
  height: 30px;
}

.theme--light.v-btn.v-btn--icon.add-details-button:hover {
  color: $color-blue-grey-lighten2 !important;
  box-shadow: inset 18px 18px 18px 18px $color-blue-grey-lighten2 !important;
  border-radius: 50%;
}

.task-input-creator {
  width: 100%;
  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    outline: none !important;
  }
}

.task-inline-creator-container:focus-within {
  border-color: $color-primary !important;
}
</style>
<style lang="scss">
.task-input-border * {
  border-width: 0 !important;
}

::placeholder {
  color: $color-blue-grey-lighten1;
  opacity: 1; /* Firefox */
}

::-ms-input-placeholder {
  /* Edge 12 -18 */
  color: $color-blue-grey-lighten1;
}
.theme--light.v-input.task-inline-input input::placeholder {
  color: $color-blue-grey-lighten1;
  font-weight: 500;
}

.task-inline-input input {
  color: $color-blue-grey-darken1;
  font-weight: 400 !important;
}
</style>
